import React from "react";
import { Eye, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { PostCard } from "@/components/PostCard";
import { useBackHandler } from "@/lib/backButton";

// Lot 779 — « Aperçu » avant de publier : la carte EXACTE du fil (PostCard figée) construite depuis l'état du composeur (`lib/previewPost`).
export const PostPreviewCard = ({ post }) => (
  <div data-testid="post-preview-card" style={{ color: "var(--yalola-text)" }}>
    <PostCard post={post} preview />
  </div>
);

// PC : boîte au-dessus du composeur.
export const PostPreviewDialog = ({ open, onOpenChange, post, onPublish, publishLabel, t }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent variant="sheet" data-testid="post-preview-dialog">
      <DialogHeader>
        <DialogTitle className="text-center font-display flex items-center justify-center gap-2"><Eye size={18} /> {t("post.preview_title", "Aperçu de ta publication")}</DialogTitle>
        <DialogDescription className="text-center text-xs">{t("post.preview_hint", "Voici ta publication telle que les autres la verront dans le fil.")}</DialogDescription>
      </DialogHeader>
      <DialogBody className="pt-3" style={{ background: "var(--yalola-bg)" }}>
        {post && <PostPreviewCard post={post} />}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" className="rounded-xl h-11" onClick={() => onOpenChange(false)} data-testid="post-preview-edit">{t("post.preview_edit", "Modifier")}</Button>
        <Button className="pill-primary rounded-xl h-11 sm:flex-1" onClick={onPublish} data-testid="post-preview-publish">{publishLabel}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Téléphone (composeur plein écran) : écran clair par-dessus, Retour Android = « Modifier ».
export const PostPreviewOverlay = ({ open, onClose, post, onPublish, publishLabel, t }) => {
  useBackHandler(open, () => { onClose(); return true; });
  if (!open || !post) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col" style={{ background: "var(--yalola-bg)", color: "var(--yalola-text)" }} data-testid="post-preview-overlay">
      <div className="shrink-0 flex items-center gap-3 px-3 pb-2.5 border-b" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)", borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}>
        <button type="button" onClick={onClose} aria-label={t("post.preview_edit", "Modifier")} className="h-10 w-10 grid place-items-center rounded-full shrink-0 active:scale-95 transition-transform" style={{ background: "var(--yalola-surface-alt)" }} data-testid="post-preview-back"><ArrowLeft size={20} /></button>
        <div className="min-w-0">
          <div className="text-sm font-semibold flex items-center gap-1.5"><Eye size={15} style={{ color: "var(--yalola-primary)" }} /> {t("post.preview_title", "Aperçu de ta publication")}</div>
          <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{t("post.preview_hint", "Voici ta publication telle que les autres la verront dans le fil.")}</div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4"><PostPreviewCard post={post} /></div>
      <div className="shrink-0 flex items-center gap-2 px-3 pt-2.5 border-t" style={{ paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)", borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}>
        <Button variant="outline" className="rounded-full h-12 flex-1" onClick={onClose} data-testid="post-preview-edit">{t("post.preview_edit", "Modifier")}</Button>
        <Button className="pill-primary rounded-full h-12 flex-1" onClick={onPublish} data-testid="post-preview-publish">{publishLabel}</Button>
      </div>
    </div>
  );
};
